import { api, type FetchOptions, type MutOpts } from "@/api"
import { queryOptions, useMutation, useQuery } from "@tanstack/react-query"

import { queryClient } from "@/lib/query-client"

const staffKeys = {
  all: ["staff"] as const,
  list: () => [...staffKeys.all, "list"] as const,
  lists: <TQuery>(query?: TQuery) => [...staffKeys.list(), { query }] as const,
  listRoles: <TQuery>(query?: TQuery) =>
    [...staffKeys.list(), "roles", { query }] as const,
  create: () => [...staffKeys.list(), "create"] as const,
  update: () => [...staffKeys.list(), "update"] as const,
  updatePassword: () => [...staffKeys.all, "update-password"] as const,
}

export const staffQueryOptions = (
  fetchOpts?: FetchOptions<"get", "/staff/">
) => {
  const query = fetchOpts?.params?.query

  return queryOptions({
    queryKey: staffKeys.lists({ query }),
    queryFn: () =>
      api.GET("/staff/", { params: { query } }).then(({ data }) => data),
    staleTime: 30_000,
  })
}

export const useStaffQuery = (fetchOpts?: FetchOptions<"get", "/staff/">) => {
  return useQuery(staffQueryOptions(fetchOpts))
}

export const useStaffRolesQuery = (
  fetchOpts?: FetchOptions<"get", "/roles/">
) => {
  const query = fetchOpts?.params?.query

  return useQuery({
    queryKey: staffKeys.listRoles(query),
    queryFn: () => {
      return api.GET("/roles/", { params: { query } }).then(({ data }) => data)
    },
  })
}

export const useCreateStaffMutation = (
  mutationOptions?: MutOpts<"post", "/staff/">
) => {
  return useMutation({
    mutationKey: staffKeys.create(),
    mutationFn: fetchOpts => {
      return api.POST("/staff/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: staffKeys.list(),
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const useUpdateStaffMutation = (
  mutationOptions?: MutOpts<"patch", "/staff/{id}/">
) => {
  return useMutation({
    mutationKey: staffKeys.update(),
    mutationFn: fetchOpts => {
      return api.PATCH("/staff/{id}/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: staffKeys.list(),
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const useDeleteStaffMutation = (
  mutationOptions?: MutOpts<"delete", "/staff/{id}/">
) => {
  return useMutation({
    mutationKey: staffKeys.update(),
    mutationFn: fetchOpts => {
      return api.DELETE("/staff/{id}/", fetchOpts)
    },

    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: staffKeys.list(),
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const useChnageStaffPasswordMutation = (
  mutationOptions?: MutOpts<"put", "/staff/change-password/">
) => {
  return useMutation({
    mutationKey: staffKeys.updatePassword(),
    mutationFn: fetchOpts => {
      return api.PUT("/staff/change-password/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: staffKeys.all,
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}
